import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, Search } from 'lucide-react';
import type { AreaOption } from '@uniticket/shared';

interface AreaComboboxProps {
  label: string;
  /** The area currently picked, or null. */
  value: AreaOption | null;
  /** Fires ONLY on selection or clearing — never while typing. See the note below. */
  onChange: (area: AreaOption | null) => void;
  options: AreaOption[];
  loading?: boolean;
  /** Debounced (300 ms) — called with the trimmed term, '' when the box is cleared. */
  onSearch: (query: string) => void;
  placeholder?: string;
  hint?: string;
}

/**
 * Search-and-pick picker for DMP build areas.
 *
 * Modelled on SplitterCombobox (same 300 ms debounce, same open/blur behaviour), with one
 * deliberate inversion: this field is PICK-ONLY. SplitterCombobox commits free text because a
 * Splitter Swap ticket asserts DMP's records are wrong, so a reference DMP doesn't know is exactly
 * what the ticket reports. A Build ticket asserts nothing about DMP — it asks a question about an
 * area that must exist — and the ticket snapshots four fields (id, name, project, release state)
 * that can only come from a real row. So typing filters; only a click commits, and it commits the
 * whole option atomically.
 */
export function AreaCombobox({
  label,
  value,
  onChange,
  options,
  loading = false,
  onSearch,
  placeholder,
  hint,
}: AreaComboboxProps) {
  const { t } = useTranslation(['tickets', 'common']);
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Clear the visible query when the parent resets the field (e.g. the user goes back and
  // switches subcategory), so a stale term can't sit above a different subcategory's results.
  useEffect(() => {
    if (!value) setQuery('');
  }, [value]);

  useEffect(() => () => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
  }, []);

  function handleChange(next: string) {
    setQuery(next);
    setOpen(true);
    // Typing invalidates any previous pick: the snapshot must always match what is displayed.
    if (value) onChange(null);

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => onSearch(next.trim()), 300);
  }

  function handleSelect(option: AreaOption) {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    setQuery(option.areaId);
    onChange(option);
    setOpen(false);
  }

  return (
    <div>
      <label className="block text-xs font-medium text-slate-600 mb-1">
        {label} <span className="text-brand-rose">*</span>
      </label>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
        <input
          type="text"
          value={query}
          onChange={(e) => handleChange(e.target.value)}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
          maxLength={100}
          placeholder={placeholder}
          className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-rose/20 focus:border-brand-rose/40"
        />
      </div>
      {hint && <p className="mt-1 text-xs text-slate-500">{hint}</p>}

      {loading && (
        <div className="flex items-center gap-2 mt-2 text-xs text-slate-400">
          <Loader2 className="w-3 h-3 animate-spin" />
          <span>{t('tickets:buildWizard.searching')}</span>
        </div>
      )}

      {open && !loading && options.length > 0 && (
        <div className="mt-2 max-h-56 overflow-y-auto border border-slate-200 rounded-lg">
          {options.map((option) => (
            <button
              key={option.areaId}
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => handleSelect(option)}
              className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 border-b border-slate-100 last:border-0 flex items-center justify-between gap-3"
            >
              <span className="min-w-0">
                <span className="font-mono text-slate-800">{option.areaId}</span>
                <span className="block text-[11px] text-slate-500 truncate">{option.areaName}</span>
              </span>
              {option.areaProject && (
                <span className="text-[11px] text-slate-400 font-mono whitespace-nowrap">
                  {option.areaProject}
                </span>
              )}
            </button>
          ))}
        </div>
      )}

      {open && !loading && options.length === 0 && (
        <p className="mt-2 text-xs text-slate-500">{t('tickets:buildWizard.noAreaMatch')}</p>
      )}
    </div>
  );
}
